import type { StressAnalytics } from '../apigen/types';

/** Applications a QA test can load. Mirrors `qa.services.targets.APP_KEYS`. */
export type QaApp = 'merch' | 'mobile' | 'video' | 'interior' | 'genapi' | 'served_models';

export const QA_APPS: QaApp[] = [
  'merch',
  'mobile',
  'video',
  'interior',
  'genapi',
  'served_models',
];

export interface QaServedModelWorkload {
  model_name: string;
  count: number;
  burst_size: number;
  burst_delay_seconds: number;
  image_url: string;
}

export type QaRunStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'cancelled'
  | 'error'
  | 'interrupted';

/**
 * Statuses a run will never move out of. `interrupted` is distinct from `error`:
 * the load it submitted was real, but the schedule never finished because the
 * hosting instance was recycled.
 */
export const QA_TERMINAL_STATUSES: QaRunStatus[] = [
  'completed',
  'cancelled',
  'error',
  'interrupted',
];

export function isQaTerminal(status: string): boolean {
  return (QA_TERMINAL_STATUSES as string[]).includes(status);
}

/**
 * One application's knobs.
 *
 * For a scan application the CSV is the load, so there is nothing to count here:
 * `burst_delay_seconds` is the only rate control, and it only bites in dummy
 * mode, where the scans go out one request at a time. Real spend is a single
 * batch of everything, which schedules nothing.
 *
 * Gen-api has no CSV — a generation call needs no scan to exist — so it alone
 * still carries `count` and `burst_size`. The server keeps normalizing both for
 * every application so older saved definitions still load; they are simply
 * ignored outside gen-api.
 */
export interface QaAppConfig {
  enabled: boolean;
  burst_delay_seconds: number;
  dummy: boolean;
  dummy_image_delay: number | null;
  dummy_other_delay: number | null;
  // gen-api only.
  count?: number;
  burst_size?: number;
  model_type?: string;
  image_width?: number;
  image_height?: number;
  prompt?: string | null;
  wait?: boolean;
  // served_models only.
  workloads?: QaServedModelWorkload[];
}

export interface QaTestDefinition {
  id: string;
  name: string;
  version: number;
  is_current: boolean;
  description: string | null;
  per_app_config: Partial<Record<QaApp, QaAppConfig>>;
  scan_ids_by_app: Partial<Record<QaApp, string[]>>;
  created_at: string | null;
  created_by: string | null;
}

export interface QaTestListResponse {
  tests: QaTestDefinition[];
}

/** What the runner recorded for one application while submitting. */
export interface QaRunTarget {
  planned: number;
  submitted: number;
  failed: number;
  bursts: number;
  /**
   * The thread back to the work. A run holds one or the other: batch ids when it
   * submitted batches, or one job id per scan when it submitted individually.
   */
  batch_ids: string[];
  job_ids?: string[];
  error: string | null;
}

/**
 * What a run spent, cached on the run rather than recomputed: the money lives in
 * five other services.
 *
 * A run stops submitting long before the work it submitted finishes, so this
 * keeps climbing after the run is terminal. `final` says nothing outstanding can
 * change it any more; until then the figure is spend so far, not an estimate.
 * Empty for a run recorded before spend was tracked.
 */
export interface QaRunCost {
  total?: number;
  per_app?: Record<string, number>;
  final?: boolean;
  updated_at?: string | null;
}

export interface QaRun {
  id: string;
  definition_id: string | null;
  definition_name: string;
  definition_version: number;
  run_number: number;
  status: QaRunStatus;
  config: {
    per_app?: Partial<Record<QaApp, QaAppConfig>>;
    scan_ids_by_app?: Partial<Record<QaApp, string[]>>;
  };
  targets: Record<string, QaRunTarget>;
  /**
   * The build each application was running when the run started, read from its
   * `/health`. Empty for a run recorded before this was captured, and missing an
   * application whose version could not be read.
   */
  backend_versions: Record<string, string>;
  cost: QaRunCost;
  calls_planned: number;
  calls_submitted: number;
  calls_failed_to_submit: number;
  last_heartbeat_at: string | null;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
}

export interface QaRunListResponse {
  runs: QaRun[];
}

export interface QaRunCreatedResponse {
  id: string;
  status: string;
  definition_name: string;
  definition_version: number;
  run_number: number;
  applications: string[];
  estimated_duration_seconds: number;
  config: Partial<Record<QaApp, QaAppConfig>>;
  warnings: string[];
}

/** One application's row in the live monitor. */
export interface QaLiveApplication {
  app: string;
  label: string;
  unit: 'scan' | 'call';
  planned: number;
  submitted: number;
  failed_to_submit: number;
  bursts: number;
  batch_ids: string[];
  error: string | null;
  pending: number;
  in_progress: number;
  completed: number;
  failed: number;
  /** Spend so far in this application, from the same read as the counts. */
  cost_usd: number;
  /** Batches that could not be read, so counts and spend are understated. */
  unreadable: number;
}

export interface QaLiveResponse {
  run: QaRun;
  applications: QaLiveApplication[];
  totals: {
    planned: number;
    submitted: number;
    failed_to_submit: number;
    pending: number;
    in_progress: number;
    completed: number;
    failed: number;
    cost_usd: number;
  };
}

export interface QaDistribution {
  count: number;
  min: number | null;
  max: number | null;
  mean: number | null;
  p50: number | null;
  p90: number | null;
  p95: number | null;
  p99: number | null;
}

export interface QaSlowJob {
  job_id: string | null;
  scan_id: string | null;
  status: string | null;
  duration_seconds: number | null;
  queue_seconds: number | null;
  error: string | null;
}

/** One scan that failed, named so it can be looked up in the application. */
export interface QaFailedJob {
  job_id: string | null;
  scan_id: string | null;
  error: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface QaAppAnalytics {
  app: string;
  label: string;
  unit: 'scan' | 'call';
  total: number;
  pending: number;
  in_progress: number;
  completed: number;
  failed: number;
  cancelled: number;
  finished: number;
  success_percentage: number | null;
  duration_seconds: QaDistribution;
  queue_seconds: QaDistribution;
  slowest: QaSlowJob[];
  errors: { error: string; count: number }[];
  /**
   * The individual failures behind the grouped `errors`, capped server-side.
   * Grouping says what went wrong; this says which scan, which is what you need
   * to go and look at one.
   */
  failed_jobs: QaFailedJob[];
  planned: number;
  submitted: number;
  failed_to_submit: number;
  bursts: number;
  batches: number;
  /** The batches this run created here, to look up in the application's history. */
  batch_ids: string[];
  cost_usd: number;
  submit_error: string | null;
}

export interface QaGenApiAnalytics extends StressAnalytics {
  app: QaApp;
  label: string;
  planned: number;
  submitted: number;
  failed_to_submit: number;
  bursts: number;
  batch_ids: string[];
  cost_usd: number;
  submit_error: string | null;
}

export interface QaRunAnalytics {
  run: QaRun;
  wall_seconds: number | null;
  applications: QaAppAnalytics[];
  /**
   * Gen-api's section is `apigen.db.stress_test_analytics` verbatim, plus the
   * submit counters — so the gen-api stress charts render against it unchanged.
   * Null when the run did not include gen-api.
   */
  genapi: QaGenApiAnalytics | null;
  /** Refreshed by this request unless the run had already settled. */
  cost: QaRunCost;
}

export interface QaApplicationOption {
  key: QaApp;
  label: string;
  unit: 'scan' | 'call';
  /** False when this deployment has no base URL for the application. */
  configured: boolean;
  base_url_env: string[];
  models?: string[];
}

export interface QaOptions {
  applications: QaApplicationOption[];
  model_types: string[];
  random_model_type: string;
  default_model_type: string;
  default_prompt: string;
  defaults: {
    count: number;
    burst_size: number;
    burst_delay_seconds: number;
  };
  limits: {
    max_count: number;
    max_burst_size: number;
    max_burst_delay_seconds: number;
    max_dummy_delay_seconds: number;
    max_image_edge: number;
  };
  async_submits_supported: boolean;
  /** Statuses a run will never leave, so the poller knows when to stop. */
  run_statuses: string[];
}

/** A test name with every run it has ever had, newest first. */
export interface QaHistoryGroup {
  name: string;
  run_count: number;
  version_count: number;
  latest_run_at: string | null;
  runs: QaRun[];
}

export interface QaHistoryResponse {
  groups: QaHistoryGroup[];
}

/** Latency spread for one served model, same shape as a QA run distribution. */
export interface QaModelDuration {
  count: number;
  min: number | null;
  max: number | null;
  mean: number | null;
  p50: number | null;
  p90: number | null;
  p95: number | null;
  p99: number | null;
}

/** One HTTP try (or FAL call) recorded by a generation pipeline. */
export interface QaServedModelAttempt {
  id: string | null;
  started_at: string | null;
  duration_seconds: number | null;
  ok: boolean;
  error: string | null;
  source: string;
  model_version: string;
  job_id: string | null;
  attempt_number: number | null;
}

/** One named model's attempt stats in a time window. */
export interface QaServedModelStats {
  model_name: string;
  calls: number;
  jobs: number;
  errors: number;
  retried_attempts: number;
  duration_seconds: QaModelDuration;
  sources: { source: string; count: number }[];
  attempts_by_number: { attempt_number: number; count: number }[];
  attempts: QaServedModelAttempt[];
}

export interface QaServedModelsResponse {
  from: string;
  to: string;
  versions: string[];
  models: QaServedModelStats[];
}

/**
 * The editable shape of one application's config, before defaults are applied.
 *
 * `count` and `burst_size` are only surfaced for gen-api, but are held for every
 * application so that opening and re-saving an older definition preserves what
 * it had rather than silently resetting it.
 */
export interface QaAppConfigDraft {
  enabled: boolean;
  count: number;
  burst_size: number;
  burst_delay_seconds: number;
  dummy: boolean;
  dummy_image_delay: number | null;
  dummy_other_delay: number | null;
  model_type?: string;
  workloads: QaServedModelWorkload[];
  scanCsvName?: string;
  scanIds: string[];
}
